import React, { useEffect, useState, useCallback } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import api, { formatApiErrorDetail } from "../lib/api";
import { Button } from "../components/ui/button";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../components/ui/select";
import { Sparkles, LogOut, ExternalLink, CalendarDays, CheckCircle2, Clock, DollarSign, Users } from "lucide-react";
import { toast } from "sonner";
import BookingsTable from "../components/BookingsTable";
import MenuManager from "../components/MenuManager";
import CalendarCard from "../components/CalendarCard";
import ReviewsManager from "../components/ReviewsManager";
import { useGoogleReviews } from "../components/Reviews";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";

const TAB_CLS = "rounded-sm text-[10px] tracking-[0.28em] uppercase data-[state=active]:bg-[#FF2079] data-[state=active]:text-white text-[#A09CA3]";

const STATUS_FILTERS = ["all", "pending", "confirmed", "completed", "cancelled"];

function StatCard({ icon: Icon, label, value, accent }) {
  return (
    <div className="glass-card rounded-sm p-5" data-testid={`stat-${label.toLowerCase().replace(/\s+/g, "-")}`}>
      <div className="flex items-center justify-between">
        <div className="eyebrow">{label}</div>
        <Icon className={`w-4 h-4 ${accent || "text-[#FF2079]"}`} />
      </div>
      <div className="font-display text-3xl text-white mt-3">{value}</div>
    </div>
  );
}

export default function AdminDashboard() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [bookings, setBookings] = useState([]);
  const [stats, setStats] = useState(null);
  const [filter, setFilter] = useState("all");
  const [loading, setLoading] = useState(true);
  const google = useGoogleReviews();

  const load = useCallback(async () => {
    try {
      const [bookingsRes, statsRes] = await Promise.all([
        api.get("/bookings"),
        api.get("/admin/stats"),
      ]);
      setBookings(bookingsRes.data);
      setStats(statsRes.data);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed to load.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const handleStatus = useCallback(async (id, status) => {
    try {
      await api.patch(`/bookings/${id}`, { status });
      toast.success("Status updated.");
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Update failed.");
    }
  }, [load]);

  const handleDelete = useCallback(async (id) => {
    try {
      await api.delete(`/bookings/${id}`);
      toast.success("Booking removed.");
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Delete failed.");
    }
  }, [load]);

  const handleLogout = async () => {
    await logout();
    navigate("/admin");
  };

  const filtered = filter === "all" ? bookings : bookings.filter((b) => b.status === filter);

  return (
    <div className="min-h-screen bg-[#0A0A0A] text-white" data-testid="admin-dashboard">
      <header className="border-b border-white/5 bg-[#0A0A0A]/80 backdrop-blur-xl sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-6 md:px-12 h-16 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2" data-testid="dashboard-logo">
            <Sparkles className="w-5 h-5 text-[#FF2079]" />
            <span className="font-display text-xl text-white">
              Pink<span className="text-[#FF2079]">Glow</span>
            </span>
            <span className="ml-3 text-[10px] tracking-[0.3em] uppercase text-[#A09CA3]">Console</span>
          </Link>
          <div className="flex items-center gap-3">
            <Link
              to="/"
              data-testid="view-site-link"
              className="hidden sm:inline-flex items-center gap-1.5 text-[10px] tracking-[0.28em] uppercase text-[#A09CA3] hover:text-white"
            >
              View Site <ExternalLink className="w-3 h-3" />
            </Link>
            <span className="hidden sm:inline text-xs text-[#A09CA3]">{user?.email}</span>
            <Button
              data-testid="logout-btn"
              onClick={handleLogout}
              variant="outline"
              className="bg-transparent border-white/10 text-white hover:bg-white/5 rounded-sm h-9 text-[10px] tracking-[0.28em] uppercase"
            >
              <LogOut className="w-3 h-3 mr-1.5" /> Sign Out
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-6 md:px-12 py-10">
        <div className="mb-10">
          <div className="eyebrow mb-3">Studio Operations</div>
          <h1 className="font-display text-4xl sm:text-5xl text-white tracking-tight">Dashboard</h1>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-12">
          <StatCard icon={Users} label="Total" value={stats?.total ?? "—"} />
          <StatCard icon={Clock} label="Pending" value={stats?.pending ?? "—"} accent="text-[#FFB6C1]" />
          <StatCard icon={CheckCircle2} label="Confirmed" value={stats?.confirmed ?? "—"} />
          <StatCard icon={CalendarDays} label="Today" value={stats?.today ?? "—"} />
          <StatCard icon={DollarSign} label="Revenue" value={stats ? `$${stats.revenue}` : "—"} accent="text-emerald-400" />
        </div>

        <Tabs defaultValue="bookings">
          <TabsList className="bg-[#141414] border border-white/10 rounded-sm mb-8 h-auto flex flex-wrap justify-start gap-1 p-1 max-w-full">
            <TabsTrigger data-testid="tab-bookings" value="bookings" className={TAB_CLS}>Bookings</TabsTrigger>
            <TabsTrigger data-testid="tab-menu" value="menu" className={TAB_CLS}>Menu & Prices</TabsTrigger>
            <TabsTrigger data-testid="tab-alerts" value="alerts" className={TAB_CLS}>Calendar & Texts</TabsTrigger>
            <TabsTrigger data-testid="tab-reviews" value="reviews" className={TAB_CLS}>Reviews</TabsTrigger>
          </TabsList>
          <TabsContent value="bookings">
        <div className="flex flex-wrap items-center justify-between gap-4 mb-6">
          <div className="eyebrow">Reservations</div>
          <Select value={filter} onValueChange={setFilter}>
            <SelectTrigger
              data-testid="filter-select"
              className="w-[180px] bg-[#141414] border-white/10 text-white rounded-sm h-10 focus:ring-0 focus:border-[#FF2079]"
            >
              <SelectValue />
            </SelectTrigger>
            <SelectContent className="bg-[#141414] border-white/10 text-white">
              {STATUS_FILTERS.map((s) => (
                <SelectItem key={s} value={s} className="focus:bg-[#FF2079]/20 focus:text-white capitalize">
                  {s}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <div className="glass-card rounded-sm overflow-hidden">
          <BookingsTable
            loading={loading}
            bookings={filtered}
            onStatus={handleStatus}
            onDelete={handleDelete}
            reviewLink={google.write_review_url}
          />
        </div>
          </TabsContent>
          <TabsContent value="menu"><MenuManager /></TabsContent>
          <TabsContent value="alerts"><CalendarCard /></TabsContent>
          <TabsContent value="reviews"><ReviewsManager /></TabsContent>
        </Tabs>
      </main>
    </div>
  );
}
